import {
  codeableConceptPredicate,
  getCodeableConcept,
} from '@ctw/shared/api/fhir/codeable-concept';
import { formatFHIRDate } from '@ctw/shared/utils/dates';
import type { CodeableConcept, Coverage } from 'fhir/r4';
import { find } from 'lodash-es';
import { FHIRModel } from './fhir-model';

export type CoverageClass = {
  plan?: string;
  group?: string;
};

const RANK_LABELS: Record<number, string> = {
  1: 'Primary',
  2: 'Secondary',
  3: 'Tertiary',
};

export class CoverageModel extends FHIRModel<Coverage> {
  public kind = 'Coverage' as const;

  public get title(): string {
    return this.payorDisplay ?? '';
  }

  public get subscriberId(): string | undefined {
    return this.resource.subscriberId;
  }

  public set subscriberId(id: string | undefined) {
    this.resource.subscriberId = id;
  }

  public get subscriber(): string | undefined {
    return this.resource.subscriber?.reference;
  }

  public set subscriber(reference: string | undefined) {
    this.resource.subscriber = { reference };
  }

  public get type(): string | undefined {
    return this.resource.type?.coding?.[0]?.code;
  }

  public set type(type: string | undefined) {
    this.resource.type = type ? getCodeableConcept(type) : undefined;
  }

  public get typeText(): string | undefined {
    return this.resource.type?.text;
  }

  public get payor(): string | undefined {
    return this.resource.payor[0]?.display;
  }

  public set payor(payor: string | undefined) {
    this.resource.payor = payor ? [{ display: payor }] : [];
  }

  public get payorDisplay(): string | undefined {
    return this.payor;
  }

  public get relationship(): string | undefined {
    return this.resource.relationship?.coding?.[0]?.code;
  }

  public set relationship(relationship: string | undefined) {
    this.resource.relationship = relationship ? getCodeableConcept(relationship) : undefined;
  }

  public get class(): CoverageClass {
    const group = find(this.resource.class, {
      type: codeableConceptPredicate('group')[0],
    })?.value;
    const plan = find(this.resource.class, {
      type: codeableConceptPredicate('plan')[0],
    })?.value;
    return { group, plan };
  }

  public set class(c: CoverageClass) {
    const classes: Array<{ value: string; type: CodeableConcept }> = [];
    if (c.plan) {
      classes.push({
        value: c.plan,
        type: getCodeableConcept('plan', 'http://terminology.hl7.org/CodeSystem/coverage-class'),
      });
    }
    if (c.group) {
      classes.push({
        value: c.group,
        type: getCodeableConcept('group', 'http://terminology.hl7.org/CodeSystem/coverage-class'),
      });
    }
    if (classes.length > 0) {
      this.resource.class = classes;
    } else {
      this.resource.class = undefined;
    }
  }

  public get order(): number | undefined {
    return this.resource.order;
  }

  public get rankLabel(): string | undefined {
    return this.order ? RANK_LABELS[this.order] : undefined;
  }

  public get status(): string {
    return this.resource.status;
  }

  public get typeDisplay(): string {
    const text = this.typeText;
    if (!text) {
      return 'Unknown';
    }
    const normalized = text.trim().toUpperCase();
    if (['NOINFORMATION', 'NO INFORMATION'].includes(normalized)) {
      return 'Unknown';
    }
    if (/MEDICAID.*HMO|HMO.*MEDICAID|MANAGED\s*CARE\s*MEDICAID/.test(normalized)) {
      return 'Medicaid HMO';
    }
    if (/MEDICAID/.test(normalized)) {
      return 'Medicaid';
    }
    if (/MEDICARE\s*ADVANTAGE/.test(normalized)) {
      return 'Medicare Advantage';
    }
    if (/MEDICARE/.test(normalized)) {
      return 'Medicare';
    }
    if (/SELF[\s-]*PAY/.test(normalized)) {
      return 'Self-Pay';
    }
    if (
      /PPO|HMO|EPO|POS|COMMERCIAL|PREFERRED\s*PROVIDER|INDEMNITY|GROUP\s*HEALTH/.test(normalized)
    ) {
      return 'Commercial';
    }
    return 'Unknown';
  }

  public get periodStart(): string | undefined {
    return this.resource.period?.start ? formatFHIRDate(this.resource.period.start) : undefined;
  }
}
